import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

type MarkdownContentBoxProps = {
  markdown?: string;
  emptyText?: string;
  containerClassName?: string;
  contentClassName?: string;
};

function isPipeTableRow(line: string) {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.endsWith('|');
}

function isGfmSeparatorRow(line: string) {
  const trimmed = line.trim();
  return /^\|\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|$/.test(trimmed);
}

function splitPipeCells(line: string) {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

function joinPipeCells(cells: string[]) {
  return `| ${cells.join(' | ')} |`;
}

function normalizePipeTableBlocks(markdown: string) {
  const lines = markdown.split(/\r?\n/);
  const result: string[] = [];

  for (let index = 0; index < lines.length; ) {
    if (!isPipeTableRow(lines[index])) {
      result.push(lines[index]);
      index += 1;
      continue;
    }

    const blockStart = index;
    while (index < lines.length && isPipeTableRow(lines[index])) {
      index += 1;
    }

    const blockLines = lines.slice(blockStart, index);
    if (blockLines.length < 2 || blockLines.some(isGfmSeparatorRow)) {
      result.push(...blockLines);
      continue;
    }

    const rowCells = blockLines.map(splitPipeCells);
    const maxColumns = Math.max(...rowCells.map((cells) => cells.length));
    const normalizedRows = rowCells.map((cells) => {
      const padded = [...cells];
      while (padded.length < maxColumns) {
        padded.push('');
      }
      return joinPipeCells(padded);
    });

    result.push(normalizedRows[0]);
    result.push(joinPipeCells(Array(maxColumns).fill('---')));
    result.push(...normalizedRows.slice(1));
  }

  return result.join('\n');
}

export const DEFAULT_MARKDOWN_CONTENT_CLASS_NAME =
  'prose prose-sm max-w-none text-slate-700 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-slate-300 [&_th]:bg-slate-100 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border [&_td]:border-slate-300 [&_td]:px-2 [&_td]:py-1 [&_img]:max-w-[300px] [&_img]:h-auto';

export function MarkdownContentBox({
  markdown,
  emptyText = 'No content available.',
  containerClassName = 'rounded-md border border-slate-200 bg-slate-50 p-3',
  contentClassName = DEFAULT_MARKDOWN_CONTENT_CLASS_NAME,
}: MarkdownContentBoxProps) {
  const normalizedMarkdown = normalizePipeTableBlocks((markdown || '').trim());

  return (
    <div className={containerClassName}>
      <div className={contentClassName}>
        {normalizedMarkdown ? (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              table: ({ ...props }) => (
                <div className="overflow-x-auto">
                  <table {...props} />
                </div>
              ),
              a: ({ ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
              img: ({ style, ...props }) => <img {...props} style={{ ...(style || {}), maxWidth: '300px', height: 'auto' }} />,
            }}
          >
            {normalizedMarkdown}
          </ReactMarkdown>
        ) : (
          emptyText
        )}
      </div>
    </div>
  );
}
